export const COMPLIANCE_TYPE_LABELS = {
  license: 'परवाना / Licence',
  noc: 'ना हरकत प्रमाणपत्र / NOC',
  registration: 'नोंदणी / Registration',
  renewal: 'नूतनीकरण / Renewal',
  inspection: 'तपासणी / Inspection',
  other: 'इतर / Other',
};

export const CASE_STATUS_LABELS = {
  draft: 'मसुदा / Draft',
  submitted: 'सादर / Submitted',
  documents_requested: 'कागदपत्रे मागितली / Documents requested',
  under_review: 'तपासणी सुरू / Under review',
  inspection_scheduled: 'स्थळ तपासणी नियोजित / Inspection scheduled',
  approved: 'मंजूर / Approved',
  issued: 'प्रमाणपत्र जारी / Issued',
  rejected: 'नामंजूर / Rejected',
  expired: 'मुदत संपली / Expired',
  suspended: 'निलंबित / Suspended',
  revoked: 'रद्द / Revoked',
  withdrawn: 'मागे घेतले / Withdrawn',
};

export const INSPECTION_STATUS_LABELS = {
  scheduled: 'नियोजित / Scheduled',
  in_progress: 'सुरू / In progress',
  completed: 'पूर्ण / Completed',
  cancelled: 'रद्द / Cancelled',
  rescheduled: 'पुनर्नियोजित / Rescheduled',
};

export const INSPECTION_OUTCOME_LABELS = {
  pending: 'प्रलंबित / Pending',
  compliant: 'अनुपालन पूर्ण / Compliant',
  non_compliant: 'अनुपालन नाही / Non-compliant',
  conditional: 'अटींसह / Conditional',
};

export const CASE_STATUSES = Object.keys(CASE_STATUS_LABELS);
export const COMPLIANCE_TYPES = Object.keys(COMPLIANCE_TYPE_LABELS);

export const ADMIN_CASE_ACTIONS = {
  submitted: ['start-review', 'request-documents', 'reject'],
  documents_requested: ['start-review'],
  under_review: ['request-documents', 'approve', 'reject'],
  inspection_scheduled: ['start-review', 'request-documents', 'approve', 'reject'],
  approved: ['issue'],
  issued: ['suspend', 'revoke'],
  suspended: ['issue', 'revoke'],
};

export const ACTION_LABELS = {
  'start-review': 'तपासणी सुरू करा / Start review',
  'request-documents': 'कागदपत्रे मागवा / Request documents',
  approve: 'मंजूर करा / Approve',
  reject: 'नामंजूर करा / Reject',
  issue: 'प्रमाणपत्र जारी करा / Issue',
  suspend: 'निलंबित करा / Suspend',
  revoke: 'रद्द करा / Revoke',
};

export const emptyRequirementDocument = () => ({
  code: '', label: '', label_marathi: '', required: true, description: '',
});

export const emptyRequirementForm = () => ({
  code: '', name: '', name_marathi: '', compliance_type: 'license', description: '',
  business_types: '', business_categories: '', constitution_types: '',
  required_documents: [], validity_months: '', requires_inspection: false,
  fee_amount: '0', fee_currency: 'INR', fee_tax_rule_code: '',
  collection_required_before_issue: false, is_active: true,
});

export const listToText = value => (Array.isArray(value) ? value.join(', ') : '');

export const textToList = value => [...new Set(String(value || '')
  .split(',')
  .map(item => item.trim())
  .filter(Boolean))];

export const requirementToForm = requirement => ({
  code: requirement?.code || '',
  name: requirement?.name || '',
  name_marathi: requirement?.name_marathi || '',
  compliance_type: requirement?.compliance_type || 'license',
  description: requirement?.description || '',
  business_types: listToText(requirement?.applies_config?.business_types),
  business_categories: listToText(requirement?.applies_config?.business_categories),
  constitution_types: listToText(requirement?.applies_config?.constitution_types),
  required_documents: (requirement?.required_documents || []).map(item => ({
    code: item.code || '', label: item.label || '', label_marathi: item.label_marathi || '',
    required: item.required !== false, description: item.description || '',
  })),
  validity_months: requirement?.validity_months ?? '',
  requires_inspection: Boolean(requirement?.requires_inspection),
  fee_amount: String(requirement?.fee_config?.amount ?? 0),
  fee_currency: requirement?.fee_config?.currency || 'INR',
  fee_tax_rule_code: requirement?.fee_config?.tax_rule_code || '',
  collection_required_before_issue: Boolean(requirement?.fee_config?.collection_required_before_issue),
  is_active: requirement?.is_active !== false,
});

export const requirementPayload = form => ({
  code: String(form.code || '').trim().toUpperCase(),
  name: String(form.name || '').trim(),
  name_marathi: String(form.name_marathi || '').trim() || null,
  compliance_type: form.compliance_type,
  description: String(form.description || '').trim() || null,
  applies_config: {
    business_types: textToList(form.business_types),
    business_categories: textToList(form.business_categories),
    constitution_types: textToList(form.constitution_types),
  },
  required_documents: (form.required_documents || []).map(document => ({
    code: String(document.code || '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
    label: String(document.label || '').trim(),
    label_marathi: String(document.label_marathi || '').trim() || null,
    required: document.required !== false,
    description: String(document.description || '').trim() || null,
  })),
  validity_months: form.validity_months === '' ? null : Number(form.validity_months),
  requires_inspection: Boolean(form.requires_inspection),
  fee_config: {
    amount: Number(form.fee_amount || 0),
    currency: 'INR',
    tax_rule_code: String(form.fee_tax_rule_code || '').trim() || null,
    collection_required_before_issue: Boolean(form.collection_required_before_issue),
  },
  is_active: Boolean(form.is_active),
});

export const formatDate = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString('en-IN');
};

export const formatDateTime = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString('en-IN');
};

export const getApiError = (error, fallback) => {
  const data = error?.response?.data;
  const details = Array.isArray(data?.details)
    ? data.details.map(item => item.message || item.code || item.field).filter(Boolean).join(', ')
    : '';
  return [data?.message || data?.error || error?.message || fallback, details].filter(Boolean).join(' — ');
};

export const downloadBlob = (response, fallbackName) => {
  const disposition = response?.headers?.['content-disposition'] || '';
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const simple = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const filename = encoded ? decodeURIComponent(encoded) : (simple || fallbackName);
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};
